// Context hooks - replacing Apollo
import { useResult } from "../../../../shared/formContext/hooks/useResult";

// Components
import ChartLegend from "../../../../components/ChartLegend/ChartLegend";
import ChartPie from "../../../../components/ChartPie/ChartPie";
import LineLegend from "../../../../components/LineLegend/LineLegend";

// Utils
import { utils } from "../../../../helpers/index";

// WP i18n
import { __ } from "@wordpress/i18n";

// React
import { useEffect, useState } from "react";

// Types
import { ChartDataItem } from "./../../types/chartData";
import {SavingsCertificatesResult} from "@blocks/savings-certificates/interfaces/savingsCertificatesTypes.ts";

/**
 * TabSavingsComposition Component
 *
 * Displays the breakdown composition of the savings certificates investment
 * Renders a pie chart with a legend and final value summary
 * Uses Context API instead of Apollo for state management
 *
 * @returns JSX element containing the composition visualization or null if no data
 */
const TabSavingsComposition = (): JSX.Element | null => {
  // Use Context hook instead of Apollo reactive variables
  const { resultData } = useResult();
  const { response } = resultData;

  // Local state for chart data
  const [chartData, setChartData] = useState<ChartDataItem[] | null>(null);
  const apiResult = response?.data.result as SavingsCertificatesResult;
  /**
   * Effect to process API response and generate chart data
   * Transforms raw composition data into chart-friendly format
   */
  useEffect(() => {
    if (apiResult) {
      setChartData([
        {
          name: __("Subscrição inicial", "df-simulators"),
          value: apiResult.initial_investment || 0,
          color: "#000046",
        },
        {
          name: __("Subscrições adicionais", "df-simulators"),
          value: apiResult.total_additional_contributions || 0,
          color: "#33336B",
        },
        {
          name: __("Juros acumulados", "df-simulators"),
          value: apiResult.gross_interest_amount || 0,
          color: "#33ADD6",
        },
        {
          name: __("Retenção IRS sobre os juros", "df-simulators"),
          value: -(apiResult.gross_amount - apiResult.net_amount || 0).toFixed(2),
          color: "#CCEBF5",
        },
      ]);
    }
  }, [response]);

  // Don't render if no chart data is available
  if (!chartData) return <p>{__("Erro ao gerar gráfico.", "df-simulators")}</p>

  return (
    <div className="flex flex-col md:grid grid-cols-8 gap-8 items-center">
      {/* Pie Chart Section */}
      <div className="col-span-3 relative p-8 md:p-0 w-full flex justify-center items-center">
        <div className="w-full max-w-[220px] aspect-square">
          <ChartPie
            data={chartData}
            title={__("Valor bruto", "df-simulators")}
            number={utils.toEuro(apiResult.gross_amount)}
          />
        </div>
      </div>

      {/* Legend and Summary Section */}
      <div className="col-span-5 w-full md:w-auto">
        <ChartLegend data={chartData} border euro_precision={2} />

        <LineLegend
          topBorder
          label={__(
            "Valor final líquido (subscrito + juros)",
            "df-simulators",
          )}
          value={utils.toEuro(apiResult.net_amount)}
        />
      </div>
    </div>
  );
};

export default TabSavingsComposition;
